import { useCallback, useMemo, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'
import { useTranslation } from 'react-i18next'

import { SelectOption } from '@percent/lemonade'
import { useQuery, getCountryNameWithOverride } from '@percent/workplace-giving/common/hooks'
import { searchOrganisations } from '@percent/workplace-giving/api/search/searchOrganisations/searchOrganisations'
import { Organisation } from '@percent/workplace-giving/api/search/searchOrganisations/searchOrganisations.types'
import { createShortLink } from '@percent/utility'
import { getShortLanguage } from '@percent/workplace-giving/utils/getShortLanguage'
import { searchVerifiedOrganisations } from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations'
import { OrgSearchFor } from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations.types'
import { mapVerifiedOrgToRegularOrg } from '@percent/workplace-giving/utils/mapVerifiedOrgToOrg/mapVerifiedOrgToOrg'

type UseOrganisationSearchQueryOptions = {
  countryCode: string
  searchOrgsWithEligibilityCheck: boolean | null
  for?: OrgSearchFor
}

export function useOrganisationSearchQuery({
  countryCode,
  searchOrgsWithEligibilityCheck,
  for: searchFor
}: UseOrganisationSearchQueryOptions) {
  const { t, i18n } = useTranslation()
  const shortLanguage = getShortLanguage(i18n)

  const [query, setQuery] = useState<string>('')
  const [debouncedQuery] = useDebounceValue(query, 200)

  const { data: searchUnverifiedData, isFetching: isFetchingUnverifiedData } = useQuery(
    ['searchNonProfits', { query: debouncedQuery, pageSize: 10, countryCode }],
    searchOrganisations,
    { enabled: !!debouncedQuery && !searchOrgsWithEligibilityCheck }
  )

  const { data: searchVerifiedData, isFetching: isFetchingVerifiedData } = useQuery(
    [
      'searchVerifiedOrganisations',
      {
        query: debouncedQuery,
        pageSize: 10,
        countryCode,
        for: searchFor
      }
    ],
    searchVerifiedOrganisations,
    { enabled: !!debouncedQuery && !!searchOrgsWithEligibilityCheck }
  )

  const getOrganisationDescription = useCallback(
    (organisation?: Organisation): string => {
      return organisation
        ? [
            organisation.website ? createShortLink(organisation.website) : undefined,
            `${t('workplace_giving.common.id')}: ${organisation.registryId}`,
            getCountryNameWithOverride(organisation.countryCode, shortLanguage)
          ]
            .filter(x => x)
            .join(' | ')
        : ''
    },
    [shortLanguage, t]
  )

  const searchResults: SelectOption[] = useMemo(() => {
    if (searchOrgsWithEligibilityCheck) {
      return searchVerifiedData
        ? searchVerifiedData.data.map(organisation => ({
            value: organisation.organisationId,
            label: organisation.displayName || organisation.name,
            description: getOrganisationDescription(mapVerifiedOrgToRegularOrg(organisation))
          }))
        : []
    }

    return searchUnverifiedData
      ? searchUnverifiedData.data.map(organisation => ({
          value: organisation.id,
          label: organisation.displayName || organisation.name,
          description: getOrganisationDescription(organisation)
        }))
      : []
  }, [searchOrgsWithEligibilityCheck, searchUnverifiedData, searchVerifiedData, getOrganisationDescription])

  const findSelectedOrganisation = useCallback(
    (id: string): Organisation | undefined => {
      if (searchOrgsWithEligibilityCheck) {
        const org = searchVerifiedData?.data.find(o => o.organisationId === id)

        return org ? mapVerifiedOrgToRegularOrg(org) : undefined
      }

      return searchUnverifiedData?.data.find(o => o.id === id)
    },
    [searchOrgsWithEligibilityCheck, searchVerifiedData, searchUnverifiedData]
  )

  return {
    query,
    setQuery,
    searchResults,
    isFetching: isFetchingVerifiedData || isFetchingUnverifiedData,
    getOrganisationDescription,
    findSelectedOrganisation
  }
}
